import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild,
    Injectable
} from '@angular/core';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MatDateFormats,
    MatNativeDateModule,
    NativeDateAdapter
} from '@angular/material/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { Subject } from 'rxjs';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { FollowupSummary } from 'src/app/shared/models/followup-summary.model';
import { formatDate } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
dayjs.extend(localeData);
dayjs().localeData();

const DAYS_PER_WEEK = 7;

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
    getFirstDayOfWeek(): number {
        return 0;
    }

    getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
        switch (style) {
            case 'long':
                return dayjs.weekdays();
            case 'short':
                return dayjs.weekdaysShort();
            case 'narrow':
                return dayjs.weekdaysShort();
        }
    }
}

@UntilDestroy()
@Component({
    selector: 'calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    providers: [{ provide: DateAdapter, useClass: CustomDateAdapter }],
    standalone: true,
    imports: [MatDatepickerModule, MatNativeDateModule]
})
export class CalendarComponent implements OnDestroy, AfterViewInit, OnChanges {
    @ViewChild(MatCalendar) calendar: MatCalendar<Date>;
    @Input() followupDates: FollowupSummary[] = [];
    @Input() plannedVisitDates: Date[] = [];
    @Input() selectedDates: Date[] = [];
    @Input() header: any = null;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() dateFilter: (date: any) => boolean;
    @Output() selectedDate: EventEmitter<any> = new EventEmitter();
    @Output() dateChanged: EventEmitter<Date> = new EventEmitter();
    today: Date;
    selected: Date;
    tmpActiveDate: Date;
    activeDate: Date;
    activeYearDate: Date;
    listenClickViewBtnFunc: Function;
    listenClickNextBtnFunc: Function;
    listenClickPrevBtnFunc: Function;
    private _cleanup$ = new Subject();

    constructor(
        @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
        private element: ElementRef,
        private renderer: Renderer2,
        private _adapter: DateAdapter<Date>,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        this.today = this._adapter.today();
        this.activeDate = this._adapter.today();
        this.activeYearDate = this._adapter.today();
        this.tmpActiveDate = this.activeDate;
    }

    ngOnChanges() {
        if (this.calendar) {
            this.calendar.stateChanges.next();
        }
    }

    ngAfterViewInit() {
        this.calendar.stateChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => setTimeout(() => this.checkCurrentView()));
        this.calendar.selectedChange.pipe(untilDestroyed(this)).subscribe((d) => {
            this.dateChanged.emit(d);
            this.selectedDate.emit(dayjs(d).format('L'));
            this.selected = d;
            this.addPointToday(this.selected);
        });
        this.calendar.monthSelected.pipe(untilDestroyed(this)).subscribe((m) => {
            this.activeDate = m;
            this.tmpActiveDate = this.activeDate;
        });
        this.calendar.yearSelected.pipe(untilDestroyed(this)).subscribe((y) => {
            this.activeDate = y;
            this.activeYearDate = y;
        });

        let btn = null;
        let btnPrev = null;
        let btnNext = null;
        if (!this.header) {
            btn = this.element.nativeElement.querySelector('.mat-calendar-period-button');
            btnPrev = this.element.nativeElement.querySelector('.mat-calendar-previous-button');
            btnNext = this.element.nativeElement.querySelector('.mat-calendar-next-button');
        } else {
            btnPrev = this.element.nativeElement.querySelector('.calendar-previous-button');
            btnNext = this.element.nativeElement.querySelector('.calendar-next-button');
        }

        if (btn) {
            this.listenClickViewBtnFunc = this.renderer.listen(btn, 'click', (event: MouseEvent) => {
                if (this.calendar.currentView === 'month') {
                    const date = this._adapter.getMonth(this.tmpActiveDate);
                    const month = this._adapter.getMonth(this.tmpActiveDate);
                    const year = this._adapter.getYear(this.activeYearDate);
                    this.activeDate = new Date(year, month, date);
                }
                this.checkCurrentView();
            });
        }

        if (btnNext) {
            this.listenClickNextBtnFunc = this.renderer.listen(btnNext, 'click', (event: MouseEvent) => {
                if (this.calendar.currentView === 'month') {
                    this.activeDate = this._adapter.addCalendarMonths(this.activeDate, 1);
                    this.dateChanged.emit(this.activeDate);
                    this.tmpActiveDate = this.activeDate;
                    this.getDates();
                }
            });
        }

        if (btnPrev) {
            this.listenClickPrevBtnFunc = this.renderer.listen(btnPrev, 'click', (event: MouseEvent) => {
                if (this.calendar.currentView === 'month') {
                    this.activeDate = this._adapter.addCalendarMonths(this.activeDate, -1);
                    this.dateChanged.emit(this.activeDate);
                    this.tmpActiveDate = this.activeDate;
                    this.getDates();
                }
            });
        }
        this.checkCurrentView();
    }

    ngOnDestroy() {
        if (this.listenClickViewBtnFunc) {
            this.listenClickViewBtnFunc();
        }
        if (this.listenClickNextBtnFunc) {
            this.listenClickNextBtnFunc();
        }
        if (this.listenClickPrevBtnFunc) {
            this.listenClickPrevBtnFunc();
        }
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    checkCurrentView() {
        if (this.calendar.currentView === 'month') {
            setTimeout(() => this.getDates());
        }
    }

    /**
     * Get datesa from calendar
     */
    getDates() {
        const dates = this.getMonthsDates();
        dates.forEach((d) => this.addPointsToDates(d));
    }

    /**
     * Get month dates from calendar
     */
    getMonthsDates() {
        const firstOfMonth = this._adapter.createDate(
            this._adapter.getYear(this.activeDate),
            this._adapter.getMonth(this.activeDate),
            1
        );
        const firstWeekOffset =
            (DAYS_PER_WEEK + this._adapter.getDayOfWeek(firstOfMonth) - this._adapter.getFirstDayOfWeek()) %
            DAYS_PER_WEEK;
        const daysInMonth = this._adapter.getNumDaysInMonth(this.activeDate);

        const dates = [];
        for (let i = 0, cell = firstWeekOffset; i < daysInMonth; i++, cell++) {
            if (cell === DAYS_PER_WEEK) {
                cell = 0;
            }
            const date = this._adapter.createDate(
                this._adapter.getYear(this.activeDate),
                this._adapter.getMonth(this.activeDate),
                i + 1
            );
            dates.push(date);
        }
        return dates;
    }

    /**
     * Add points to each date
     */
    addPointsToDates(date: Date) {
        const followupsForDate = this.followupDates.find((d) => d.name === formatDate(date, 'MM/dd/yyyy', 'en-US'));
        const selectedDates = this.selectedDates.map((d) => String(d.getTime()));
        const isSelected = selectedDates.includes(String(date.getTime()));

        //Set all days as low
        const aria = this._adapter.format(date, this._dateFormats.display.dateA11yLabel);
        const el = this.element.nativeElement.querySelector(`[aria-label="${aria}"]`);
        el.querySelector('.mat-calendar-body-cell-content').classList.add('extra-point-low');

        this.addPoint(followupsForDate ? followupsForDate.count : 0, isSelected, aria);
    }

    /**
     * Remove all points
     */
    removeAllPoints() {
        const els = this.element.nativeElement.querySelectorAll('[aria-label]');
        els.forEach((element) => {
            const point = element.querySelector('.mat-calendar-body-cell-content');
            if (point) {
                point.classList.remove('extra-point');
                point.classList.remove('planned-point');
                point.classList.remove('info-point');
            }
        });
    }

    /**
     * Add points to the calendar with classes
     * @param isExtraLow
     * @param isExtraMed
     * @param isExtraHigh
     * @param isSelected
     * @param aria
     */
    addPoint(followupCount: number, isSelected: boolean, aria: string) {
        const el = this.element.nativeElement.querySelector(`[aria-label="${aria}"]`);
        if (el && this.calendar.currentView === 'month') {
            if (followupCount < 21) {
                el.querySelector('.mat-calendar-body-cell-content').classList.add('extra-point-low');
            } else if (followupCount < 41) {
                el.querySelector('.mat-calendar-body-cell-content').classList.remove('extra-point-low');
                el.querySelector('.mat-calendar-body-cell-content').classList.add('extra-point-med');
            } else {
                el.querySelector('.mat-calendar-body-cell-content').classList.remove('extra-point-low');
                el.querySelector('.mat-calendar-body-cell-content').classList.add('extra-point-high');
            }
            if (isSelected) {
                el.querySelector('.mat-calendar-body-cell-content').classList.add('selected-date');
            }
        }
    }

    /**
     * Selected day
     * @param date
     */
    addPointToday(date) {
        const els = this.element.nativeElement.querySelectorAll('[aria-label]');
        els.forEach((element) => {
            const point = element.querySelector('.mat-calendar-body-cell-content');
            if (point) {
                point.classList.remove('selected-date');
            }
        });
        const slectedDate = String(this.selected.getTime()).includes(String(date.getTime()));
        const aria = this._adapter.format(date, this._dateFormats.display.dateA11yLabel);

        const el = this.element.nativeElement.querySelector(`[aria-label="${aria}"]`);
        if (slectedDate && el) {
            el.querySelector('.mat-calendar-body-cell-content').classList.add('selected-date');
        }
    }
}
